import Fuse from "fuse.js";
import { computed, watchSyncEffect } from "vue";
import { safeArray, size } from "@/helpers/functions/ArrayHelpers";
import { stripAccents } from "@/helpers/functions/StringHelpers";

/**
 * Normalizes Fuse results across versions and when `includeScore` is enabled.
 *
 * @template T
 * @param {T | { item: T, score?: number, refIndex?: number }} result
 * @returns {T}
 */
function unwrapResult(result) {
  if (
    result &&
    typeof result === "object" &&
    Object.prototype.hasOwnProperty.call(result, "item") &&
    (Object.prototype.hasOwnProperty.call(result, "score") ||
      Object.prototype.hasOwnProperty.call(result, "refIndex"))
  ) {
    return result.item;
  }

  return result;
}

/**
 * Creates a reactive Fuse.js search over a reactive list.
 *
 * `options` is forwarded unchanged to the Fuse constructor.
 *
 * @template T
 * @param {import("vue").Ref<T[]>} source
 * @param {import("vue").Ref<string>} searchText
 * @param {Object} [options={}]
 * @returns {{
 *   list: import("vue").ComputedRef<T[]>,
 *   search: (searchText?: string) => void,
 *   clear: () => void,
 *   count: import("vue").ComputedRef<number>
 * }}
 */
export function useFuse(source, searchText, options = {}) {
  const fuse = new Fuse([], options);

  const list = computed(() => {
    const collection = safeArray(source.value);
    const query = stripAccents(searchText.value || "").trim();

    if (!query) return collection;

    return fuse.search(searchText.value).map(unwrapResult);
  });

  const count = computed(() => size(list.value));

  watchSyncEffect(() => {
    fuse.setCollection(safeArray(source.value));
  });

  function search(query = "") {
    searchText.value = String(query || "");
  }

  function clear() {
    searchText.value = "";
  }

  return {
    list,
    count,
    search,
    clear,
  };
}
